import { createContext, useContext } from 'react';

/** Perfis de acesso (grupos Cognito). */
export type UserProfile = 'consulta' | 'editor' | 'admin';

/** Usuário autenticado (perfil resolvido via GET /me). */
export interface AuthUser {
  readonly sub: string;
  readonly email: string | null;
  readonly profiles: readonly UserProfile[];
  /** true quando a sessão passou pelo desafio MFA TOTP (RN-M1-001). */
  readonly mfaVerified: boolean;
}

/** Estado de sessão exposto pela aplicação. */
export interface AuthState {
  /** Usuário autenticado, ou null quando anônimo. */
  readonly user: AuthUser | null;
  /** true enquanto o estado inicial de auth ainda é resolvido. */
  readonly loading: boolean;
  /** Token de acesso atual (em memória), ou undefined. */
  readonly token: string | undefined;
}

/**
 * Ações de autenticação expostas às telas de login/MFA/logout. Separadas do
 * estado para que componentes que só leem o estado não re-renderizem quando as
 * ações (estáveis) mudam.
 */
/**
 * Próximo passo do fluxo de login:
 *  - `ok`: sessão concluída;
 *  - `mfa`: desafio TOTP (a UI navega para /mfa);
 *  - `nova-senha`: primeiro acesso, troca da senha temporária (/nova-senha);
 *  - `cadastro-mfa`: admin sem TOTP cadastrado (ADR-021) — /cadastro-mfa.
 */
export type ResultadoLogin = 'ok' | 'mfa' | 'nova-senha' | 'cadastro-mfa';

/** Dados para o QR do cadastro do TOTP. */
export interface CadastroTotp {
  /** Segredo base32 (também exibido para digitação manual). */
  readonly segredo: string;
  /** URI `otpauth://totp/...` codificada no QR. */
  readonly uri: string;
}

export interface AuthActions {
  /** Autentica com e-mail e senha e diz qual é o próximo passo. */
  login(email: string, password: string): Promise<ResultadoLogin>;
  /** Conclui o desafio MFA TOTP e resolve a sessão. */
  confirmMfa(code: string): Promise<void>;
  /** Define a nova senha no primeiro acesso e diz qual é o próximo passo. */
  definirNovaSenha(novaSenha: string): Promise<ResultadoLogin>;
  /** Inicia o cadastro do TOTP (segredo + URI do QR). */
  iniciarCadastroTotp(emissor: string): Promise<CadastroTotp>;
  /**
   * Verifica o primeiro código e ativa o TOTP. Encerra a sessão restrita: o
   * admin entra de novo, agora com o desafio TOTP e o perfil completo.
   */
  confirmarCadastroTotp(code: string): Promise<void>;
  /** Encerra a sessão local (limpa tokens em memória). */
  logout(): void;
}

/**
 * Contexto de autenticação. O provider concreto (integração Cognito SDK) é o
 * `AuthProvider`. O valor default representa a sessão anônima em resolução.
 */
export const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  token: undefined,
});

/** Contexto de ações de autenticação (login/MFA/logout). */
export const AuthActionsContext = createContext<AuthActions | null>(null);

/** Hook de acesso ao estado de autenticação. */
export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/** Hook de acesso às ações de autenticação. Lança fora do provider. */
export function useAuthActions(): AuthActions {
  const ctx = useContext(AuthActionsContext);
  if (!ctx) {
    throw new Error('useAuthActions deve ser usado dentro de um AuthProvider.');
  }
  return ctx;
}

/** Verifica se o usuário possui pelo menos um dos perfis exigidos. */
export function hasAnyProfile(
  state: AuthState,
  required: readonly UserProfile[],
): boolean {
  if (!state.user) {
    return false;
  }
  return required.some((p) => state.user!.profiles.includes(p));
}
